// useHandoffOptions — runtime-tunable handoff option schemas (RET-1069 V2.1).
//
// Reads the PostHog feature flag `lead_agent_handoff_options`. The flag's
// JSON payload maps entry-point keys ('tomar_control', 'apagar_bot_chat',
// 'desactivar_workspace') to surface schemas with inline multi-language
// labels. When the flag is missing, malformed, or PostHog hasn't loaded
// yet, falls back to the baked-in DEFAULT_HANDOFF_SCHEMAS.
//
// Why a hook (not a top-level constant): operators tune option lists in
// the PostHog UI without a redeploy. `posthog.onFeatureFlags` fires when
// the flag cache updates (after initial fetch or PostHog refresh); we
// re-read and propagate via setState so consumers re-render with the
// new options. The shape validation is strict — any malformed entry
// falls back to the default for that surface, never crashing the modal.
//
// V2.1 payload shape (PostHog UI):
// {
//   "tomar_control": {
//     "q1Title": { "es": "...", "en": "...", "br": "..." },
//     "q1Subtitle": { "es": "...", "en": "...", "br": "..." },
//     "mandatory": false,
//     "options": [
//       { "category": "...", "labels": { "es": "...", "en": "...", "br": "..." } },
//       ...
//     ]
//   },
//   "apagar_bot_chat":     { ..., "subQuestion": {...}, "q2Label": {...}, "options": [...] },
//   "desactivar_workspace": { ... }
// }

import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { DEFAULT_HANDOFF_SCHEMAS } from './handoffReasonOptions.defaults';
import type {
  tHandoffReasonEntryPoint,
  tHandoffReasonSchema,
  tLocaleLabel,
  tReasonOption,
} from './handoffReasonOptions.types';

const FLAG_NAME = 'lead_agent_handoff_options';

type tSchemasByEntryPoint = Record<tHandoffReasonEntryPoint, tHandoffReasonSchema>;

// Strict runtime validators — refuse to accept anything that doesn't
// match the tReasonOption / tHandoffReasonSchema shape exactly. Lets
// us land arbitrary PostHog JSON without crashing the modal. Any
// surface entry that fails validation falls back to its default —
// Producto can ship one surface at a time without affecting the others.

function isLocaleLabel(x: unknown): x is tLocaleLabel {
  if (!x || typeof x !== 'object') return false;
  const l = x as Record<string, unknown>;
  // `es` is required (Spanish-first), `en` and `br` optional.
  if (typeof l.es !== 'string' || l.es.length === 0) return false;
  if (l.en !== undefined && typeof l.en !== 'string') return false;
  if (l.br !== undefined && typeof l.br !== 'string') return false;
  return true;
}

function isReasonOption(x: unknown): x is tReasonOption {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (typeof o.category !== 'string' || o.category.length === 0) return false;
  if (!isLocaleLabel(o.labels)) return false;
  if (o.subOptions !== undefined) {
    if (!Array.isArray(o.subOptions)) return false;
    if (!o.subOptions.every(isReasonOption)) return false;
  }
  if (o.showTextarea !== undefined && typeof o.showTextarea !== 'boolean') {
    return false;
  }
  return true;
}

function isSchema(x: unknown): x is tHandoffReasonSchema {
  if (!x || typeof x !== 'object') return false;
  const s = x as Record<string, unknown>;
  if (!isLocaleLabel(s.q1Title)) return false;
  if (typeof s.mandatory !== 'boolean') return false;
  if (!Array.isArray(s.options)) return false;
  if (!s.options.every(isReasonOption)) return false;
  if (s.q1Subtitle !== undefined && !isLocaleLabel(s.q1Subtitle)) return false;
  if (s.subQuestion !== undefined && !isLocaleLabel(s.subQuestion)) return false;
  if (s.q2Label !== undefined && !isLocaleLabel(s.q2Label)) return false;
  return true;
}

// Reads + validates the payload for all three entry points. Returns
// null when PostHog isn't initialized or the payload is unusable, so
// the caller can fall back to defaults wholesale.
function readSchemasFromPostHog(): tSchemasByEntryPoint | null {
  try {
    const payload = posthog.getFeatureFlagPayload(FLAG_NAME);
    if (!payload || typeof payload !== 'object') return null;
    const obj = payload as Record<string, unknown>;
    // Per-entry-point fallback: a malformed payload entry for one
    // surface doesn't corrupt the others. The dashboard payload-builder
    // can ship one surface at a time.
    return {
      tomar_control: isSchema(obj.tomar_control)
        ? obj.tomar_control
        : DEFAULT_HANDOFF_SCHEMAS.tomar_control,
      apagar_bot_chat: isSchema(obj.apagar_bot_chat)
        ? obj.apagar_bot_chat
        : DEFAULT_HANDOFF_SCHEMAS.apagar_bot_chat,
      desactivar_workspace: isSchema(obj.desactivar_workspace)
        ? obj.desactivar_workspace
        : DEFAULT_HANDOFF_SCHEMAS.desactivar_workspace,
    };
  } catch {
    // PostHog not initialized or threw — fall back wholesale.
    return null;
  }
}

// Returns the current schemas keyed by entry point. Consumers usually
// just index it: `schemas[pendingContext.entryPoint]`. The map is
// stable across renders unless the PostHog flag payload changes.
export function useHandoffOptions(): tSchemasByEntryPoint {
  const [schemas, setSchemas] = useState<tSchemasByEntryPoint>(
    () => readSchemasFromPostHog() ?? DEFAULT_HANDOFF_SCHEMAS,
  );

  useEffect(() => {
    let unsub: (() => void) | undefined;
    try {
      // posthog-js fires onFeatureFlags once on initial fetch + on every
      // reloadFeatureFlags() / identify(). Re-reading on each tick keeps
      // the modal current without polling.
      unsub = posthog.onFeatureFlags(() => {
        const fresh = readSchemasFromPostHog();
        if (fresh) setSchemas(fresh);
      });
    } catch {
      // PostHog not available — stay on defaults. No-op cleanup.
    }
    return () => {
      try {
        unsub?.();
      } catch {
        // ignore
      }
    };
  }, []);

  return schemas;
}
